import {Image, Link, useCart, useCartLine} from '@shopify/hydrogen';
import {format, parse, set} from 'date-fns';
import {formatInTimeZone} from 'date-fns-tz';

import {useDrawerContext} from '~/components';
import {DISPLAY_TIMEZONE, formatNumber} from '~/utils';

const reformatDateString = ({
  date,
  fromFormat,
  toFormat,
}: {
  date: string;
  fromFormat?: string;
  toFormat: string;
}) =>
  fromFormat
    ? formatInTimeZone(
        parse(date, fromFormat, new Date()),
        DISPLAY_TIMEZONE,
        toFormat,
      )
    : formatInTimeZone(new Date(date), DISPLAY_TIMEZONE, toFormat);

export function CartLineItem({
  onEditItem,
  isLoading = false,
  isError = false,
}: {
  isLoading?: boolean;
  isError?: boolean;
  onEditItem: (lineId: string) => void;
}) {
  const {linesRemove, lines} = useCart();
  const {id: lineId, quantity, merchandise, attributes, cost} = useCartLine();
  const {setIsOpenCart} = useDrawerContext();
  //We render surge fees inline with the associated item

  if (merchandise.product.handle === 'surge-fee') {
    return <></>;
  }
  const tfrSellingPrice = Number(
    attributes.find((attr) => attr.key === '_tfr_booking_price')?.value ||
      cost.totalAmount.amount,
  );

  const isGift =
    attributes.find((attr) => attr.key === '_tfr_is_gift')?.value === 'true';

  const tfrLineId = attributes.find(
    (attr) => attr.key === '_tfr_line_id',
  )?.value;
  const associatedSurgeFeeLines = tfrLineId
    ? lines.filter(
        (l) =>
          tfrLineId ==
          l.attributes.find((a) => a.key === '_for_tfr_line_id')?.value,
      )
    : [];
  const giftFor = attributes.find(
    (attr) => attr.key === '_gift_to_name',
  )?.value;

  const giftFrom = attributes.find(
    (attr) => attr.key === '_gift_from_name',
  )?.value;

  const giftMessage = attributes.find(
    (attr) => attr.key === '_gift_message',
  )?.value;

  const giftImage = attributes.find((attr) => attr.key === '_gift_card')?.value;

  let date = '-';

  const startTime = attributes.find(
    (attr) => attr.key === '_start_time',
  )?.value;
  if (startTime) {
    date = isGift
      ? formatInTimeZone(new Date(startTime), DISPLAY_TIMEZONE, 'dd MMM yyyy')
      : reformatDateString({
          date: startTime,
          // fromFormat: 'MMM dd yyyy, hh:mm a',
          toFormat: 'd MMM yyyy, h:mm a',
        });
  }

  const onClose = () => setIsOpenCart(false);

  return (
    <li key={lineId} className="pb-1">
      <div className="flex gap-4 text-acai-900">
        <div className="flex-shrink">
          {isGift ? (
            <div className="h-24 w-24 rounded-xl border md:h-28 md:w-28">
              {giftImage && (
                <Image
                  src={giftImage}
                  alt="Gift Card Image"
                  className="block h-full w-full rounded-xl object-cover"
                  width={112}
                  height={112}
                />
              )}
            </div>
          ) : merchandise.image ? (
            <Link
              to={`/products/${merchandise.product.handle}`}
              className="text-xl"
              onClick={onClose}
            >
              <Image
                width={112}
                height={112}
                widths={[112]}
                data={merchandise.image}
                loaderOptions={{
                  scale: 2,
                  crop: 'center',
                }}
                className="h-24 w-24 rounded-xl border object-cover object-center md:h-28 md:w-28"
              />
            </Link>
          ) : (
            <div className="h-6 w-24 rounded-xl border bg-[#e9d3d3] md:w-28" />
          )}
        </div>
        <div className="flex flex-grow flex-col gap-2">
          <div className="flex flex-grow justify-between">
            <div className="flex flex-col justify-between text-start">
              <div className="flex flex-col gap-1">
                <Link
                  to={`/products/${merchandise.product.handle}`}
                  className="text-xl"
                  onClick={onClose}
                >
                  {merchandise.product.title}
                </Link>
                <div className="flex flex-col text-[10px] sm:text-sm">
                  {isGift ? (
                    <div className="mb-2">
                      <p>For {giftFor}</p>
                      <p>From {giftFrom}</p>
                      {date && <p>Email Send Date: {date}</p>}
                      <p className="mt-1 font-bold">Message</p>
                      <p>{giftMessage || '-'}</p>
                    </div>
                  ) : (
                    <>
                      {date && <span className="pb-1">{date}</span>}
                      {(merchandise?.selectedOptions || [])
                        .filter((option) => option.value !== '-')
                        .map((option) => (
                          <span key={option.value}>{option.value}</span>
                        ))}
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="flex flex-col py-1 text-[12px] sm:text-sm">
              <div>
                {formatNumber(tfrSellingPrice)} {cost.totalAmount.currencyCode}
              </div>
              {associatedSurgeFeeLines.map((l) => (
                <div key={l.id} className="text-red-700">
                  {`(+${formatNumber(l.cost.totalAmount.amount)} ${
                    l.cost.totalAmount.currencyCode
                  })`}
                </div>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2 text-[10px] sm:text-sm">
            {!isGift && (
              <div className="text-copy flex justify-start">
                <button
                  type="button"
                  onClick={() => onEditItem(lineId)}
                  className="underline underline-offset-4"
                >
                  {isLoading
                    ? 'Loading...'
                    : isError
                    ? 'Error loading product'
                    : 'Change Timeslot'}
                </button>
              </div>
            )}
            <button
              type="button"
              onClick={() =>
                linesRemove([
                  lineId,
                  ...associatedSurgeFeeLines.map((f) => f.id),
                ])
              }
              className="underline underline-offset-4"
            >
              Remove
            </button>
            <div>Quantity: ({quantity})</div>
          </div>
        </div>
      </div>
      {/* {merchandise.product.title.includes('Gift') && (
        <div className="flex w-full flex-col space-y-2 pt-4">
          <div className="flex w-full">
            <span className="text-sm">Special instruction to seller</span>
          </div>
          <textarea
            className="relative w-full border-[1px] border-black bg-white py-4 pl-3 pr-10 text-left shadow-sm focus:border-black focus:outline-none focus:ring-0 focus:ring-black sm:text-sm"
            name="note"
            placeholder="Input instruction here..."
          ></textarea>
        </div>
      )} */}
    </li>
  );
}

// function CartLineQuantityAdjust({
//   lineId,
//   quantity,
// }: {
//   lineId: string;
//   quantity: number;
// }) {
//   return (
//     <>
//       <label htmlFor={`quantity-${lineId}`} className="sr-only">
//         Quantity, {quantity}
//       </label>
//       <div className="flex items-center rounded border">
//         <CartLineQuantityAdjustButton
//           adjust="decrease"
//           aria-label="Decrease quantity"
//           className="h-10 w-10 text-primary/50 transition hover:text-primary disabled:cursor-wait"
//         >
//           &#8722;
//         </CartLineQuantityAdjustButton>
//         <CartLineQuantity as="div" className="px-2 text-center" />
//         <CartLineQuantityAdjustButton
//           adjust="increase"
//           aria-label="Increase quantity"
//           className="h-10 w-10 text-primary/50 transition hover:text-primary disabled:cursor-wait"
//         >
//           &#43;
//         </CartLineQuantityAdjustButton>
//       </div>
//     </>
//   );
// }
